import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { PATHS } from 'src/common/constants';
import AssetDocuments from './AssetDocuments';
import AssetNotes from './AssetNotes';
import AssetMaintenance from './AssetMaintenanceGuide';
import Breadcrumb from '../../components/Breadcrumb';
import HeadingWithDetails from '../../components/HeadingWithDetails';
import Metrics from '../../components/Metrics';
import Tabs from '../../components/Tabs';
import Card from '../../components/Card';
import Label from '../../components/Label/Label';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { getAssetTabSkeleton } from 'src/pages/Asset/skeletons/AssetTabSkeleton';
import {
  clearFaqsNotesDocuments,
  clearNotes,
} from 'src/store/actions/faqsNotesDocuments';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import AssetFaqs from './AssetFaqs';
import { variables } from 'src/Tenants';
import ItemSpecifications from '../Item/ItemSpecifications';
import { clearFndFormData } from 'src/store/actions/fndForm';
import { useSpecifications } from 'src/SharedCustomHooks/UseSpecifications/useSpecifications';
import {
  useNotesAPI,
  UseNotesAPIReturn,
} from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import { useMaintenanceGuides } from 'src/SharedCustomHooks/UseMaintenanceGuides/useMaintenanceGuides';
import {
  useDocumentsAPI,
  UseFaqsNotesDocumentsAPIReturn,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/useDocumentsAPI';
import useFaqOperations, {
  UseFaqOperationsReturn,
} from 'src/components/Faqs/CustomHooks/useFaqOperations';
import CalloutCard from 'src/components/CalloutCard/CalloutCard';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';
import { useAssetAPI } from 'src/pages/Asset/Hooks/useAssetAPI';

interface AssetSkeletonViewProps {
  suite: any;
  property: any;
}

const AssetSkeletonView = ({ suite, property }: AssetSkeletonViewProps) => {
  const skeletonBadge = <Skeleton variant="text" width="1.4rem" />;

  const breadcrumbNode = suite && property ? (
    <Breadcrumb
      to={{
        pathname: PATHS.SUITES_VIEW.replace(
          ':id',
          property.id + '&' + suite.id
        ),
        state: {
          suite: suite,
          property: property,
          onNavigationBack: true,
        },
      }}
      label={variables.Suite + ' ' + (suite.number || '')}
    />
  ) : null;

  return (
    <WithNavigation
      title="Asset"
      top={
        <HeadingWithDetails
          style={{ backgroundImage: 'url(/assets/banner-property.png)' }}
          breadcrumb={breadcrumbNode}
          headline={<Skeleton variant="text" width="14rem" />}
          items={[
            <Card
              body={
                <>
                  MAKE: <Skeleton variant="text" width="6rem" /> <br />
                  MODEL: <Skeleton variant="text" width="6rem" /> <br />
                  INSTALLED ON: <Skeleton variant="text" width="6rem" />
                </>
              }
            />,
            <Metrics
              items={[
                { key: 'Active', value: <Skeleton variant="text" width="2rem" /> },
                { key: 'Archived', value: <Skeleton variant="text" width="2rem" /> },
              ]}
              title={null}
            />,
            <Card
              title="Warranty Expiration"
              body={<Skeleton variant="text" width="6rem" />}
            />,
            <Card
              title="Type/Room"
              body={<Skeleton variant="text" width="6rem" />}
            />,
          ]}
          actions={null}
        />
      }
      bottom={
        <Tabs
          activeTab={0}
          items={[
            { label: 'Specifications', count: skeletonBadge, content: getAssetTabSkeleton('Specifications') },
            { label: 'Manuals & Documents', count: skeletonBadge, content: getAssetTabSkeleton('Manuals & Documents') },
            { label: 'Maintenance Reminders', count: skeletonBadge, content: getAssetTabSkeleton('Maintenance Reminders') },
            { label: 'FAQs', count: skeletonBadge, content: getAssetTabSkeleton('FAQs') },
          ]}
        />
      }
    />
  );
};

const AssetInner = (props: any) => {
  const { asset, area } = props;

  const user: any = useSelector(selectAuthenticatedUser);

  const faqOperations: UseFaqOperationsReturn = useFaqOperations({
    itemId: asset?.item?.id,
    userId: user?.id,
    entityType: 'Asset',
    assetId: asset?.id,
  });

  const { faqs }: UseFaqOperationsReturn = faqOperations;

  const {
    guides: maintenanceGuides,
    loading: maintenanceGuidesLoading,
    saveGuide,
    updateGuide,
    deleteGuide,
    error,
    setSearchTerm,
    searchTerm,
  } = useMaintenanceGuides(asset?.item?.id || '', {
    source: 'Asset',
    userId: user?.id || '',
    userFirstName: user?.firstname || '',
    userLastName: user?.lastname || '',
  });

  const {
    documents,
    fetchDocuments,
    loadingDocs,
    isSaving,
    isDeleting,
    saveDocument,
    deleteDocument,
  }: UseFaqsNotesDocumentsAPIReturn = useDocumentsAPI();

  const specificationsAPI = useSpecifications(asset?.item?.id);
  const { specifications, allSpecifications } = specificationsAPI;

  const notesAPI: UseNotesAPIReturn = useNotesAPI(user);
  const { notes } = notesAPI;

  const dispatch = useDispatch();

  const { suite, property } = props.location.state;
  const activeTab = props.location?.state?.activeTab || 0;

  useEffect(() => {
    const initializeComponent = async () => {
      dispatch(clearFndFormData());
      dispatch(clearNotes());
      dispatch(clearFaqsNotesDocuments());

      if (asset?.id && asset?.item?.id) {
        await fetchDocuments(asset.item.id);
        void notesAPI.fetchNotes(asset.id);
      }
    };

    void initializeComponent();
  }, []);


  const noteVisibleToHomeownerOccupantRealtor: boolean =
    Array.isArray(notes) &&
    notes.some((n: any) => n?.noteVisibleToHomeownerOccupantRealtor === true);

  const homeOwnerCheck: boolean =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === 'Home Owner' ||
      user.roles[0].role.name === 'Realtor' ||
      user.roles[0].role.name === 'Occupant');

  const noSpecificationTabs: any = [
    {
      label: 'Manuals & Documents',
      count: documents.length.toString(),
      content: (
        <AssetDocuments
          asset={asset}
          documents={documents}
          loadingDocs={loadingDocs}
          isSaving={isSaving}
          isDeleting={isDeleting}
          saveDocument={saveDocument}
          deleteDocument={deleteDocument}
        />
      ),
    },
    {
      label: 'Maintenance Reminders',
      count: maintenanceGuides.length.toString(),
      content: (
        <AssetMaintenance
          asset={asset}
          maintenanceGuides={maintenanceGuides}
          loading={maintenanceGuidesLoading}
          saveGuide={saveGuide}
          updateGuide={updateGuide}
          deleteGuide={deleteGuide}
          error={error}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
        />
      ),
    },
    {
      label: 'FAQs',
      count: faqs ? faqs.length.toString() : '0',
      content: <AssetFaqs asset={asset} faqOperations={faqOperations} />,
    },
    homeOwnerCheck && noteVisibleToHomeownerOccupantRealtor === false
      ? {}
      : {
          label: 'Notes',
          count: notes?.length?.toString() || '0',
          content: (
            <AssetNotes notesAPI={notesAPI} entityId={asset.id} />
          ),
        },
  ];

  const withSpecificationTabs: any = [
    {
      label: 'Specifications',
      count: specifications.length.toString(),
      content: (
        <ItemSpecifications
          item={asset.item}
          specificationsAPI={specificationsAPI}
        />
      ),
    },
    {
      label: 'Manuals & Documents',
      count: documents.length.toString(),
      content: (
        <AssetDocuments
          asset={asset}
          documents={documents}
          loadingDocs={loadingDocs}
          isSaving={isSaving}
          isDeleting={isDeleting}
          saveDocument={saveDocument}
          deleteDocument={deleteDocument}
        />
      ),
    },
    {
      label: 'Maintenance Reminders',
      count: maintenanceGuides.length.toString(),
      content: (
        <AssetMaintenance
          asset={asset}
          maintenanceGuides={maintenanceGuides}
          loading={maintenanceGuidesLoading}
          saveGuide={saveGuide}
          updateGuide={updateGuide}
          deleteGuide={deleteGuide}
          error={error}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
        />
      ),
    },
    {
      label: 'FAQs',
      count: faqs ? faqs.length.toString() : '0',
      content: <AssetFaqs asset={asset} faqOperations={faqOperations} />,
    },
    homeOwnerCheck && noteVisibleToHomeownerOccupantRealtor === false
      ? {}
      : {
          label: 'Notes',
          count: notes?.length?.toString() || '0',
          content: (
            <AssetNotes notesAPI={notesAPI} entityId={asset.id} />
          ),
        },
  ];

  if (asset) {
    return (
      <>
        <WithNavigation
          title={asset.item && asset.item.name}
          top={
            <HeadingWithDetails
              style={{
                backgroundImage: 'url(/assets/banner-property.png)',
              }}
              breadcrumb={
                <Breadcrumb
                  to={{
                    pathname: PATHS.SUITES_VIEW.replace(
                      ':id',
                      property.id + '&' + suite.id
                    ),
                    state: {
                      suite: suite,
                      property: property,
                      onNavigationBack: true,
                    },
                  }}
                  label={variables.Suite + ' ' + suite.number}
                />
              }
              headline={asset.item && asset.item.name}
              items={[
                <Card
                  body={
                    <>
                      MAKE:{' '}
                      {asset.item && asset.item.makeName
                        ? asset.item.makeName
                        : '—'}{' '}
                      <br />
                      MODEL:{' '}
                      {asset.item && asset.item.model
                        ? asset.item.model
                        : '—'}{' '}
                      <br />
                      Asset Warranty Start Date:{' '}
                      {asset.dateOfPurchase && asset.dateOfPurchase
                        ? moment(asset.dateOfPurchase).format('MM/DD/YYYY')
                        : ''}
                    </>
                  }
                />,
                <Metrics
                  items={[
                    {
                      key: 'Active',
                      value: asset.serviceActive,
                    },
                    {
                      key: 'Archived',
                      value: asset.serviceArchived,
                    },
                  ]}
                  title={null}
                />,
                <Card
                  title="Warranty Expiration"
                  body={
                    <Label
                      color={
                        asset.warrantDays < 30
                          ? 'red'
                          : asset.warrantDays < 60
                            ? 'yellow'
                            : 'teal'
                      }
                      label={
                        asset.warrantExpiration && asset.warrantExpiration
                          ? moment(asset.warrantExpiration).format('MM/DD/YYYY')
                          : ''
                      }
                    />
                  }
                />,
                <Card
                  title="Type/Room"
                  body={
                    <>
                      {asset.item && asset.item.typeName
                        ? asset.item.typeName
                        : ''}
                      /{area.customLabel ? area.customLabel : area.name}
                    </>
                  }
                />,
              ]}
              actions={
                homeOwnerCheck ? null : (
                  <CalloutCard
                    icon_type="info"
                    maxWidth="40rem"
                    message={
                      <>
                        To edit or delete this asset, please visit the{' '}
                        <a
                          href="/app/catalog"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontWeight: 600,
                            textDecoration: 'underline',
                            textDecorationColor: '#590fa6',
                          }}
                        >
                          Catalogue
                        </a>{' '}
                        view.
                      </>
                    }
                  />
                )
              }
            />
          }
          bottom={
            <Tabs
              activeTab={activeTab}
              items={
                (user.roles[0].role.name === 'Home Owner' ||
                  user.roles[0].role.name === 'Realtor') &&
                allSpecifications.length === 0
                  ? noSpecificationTabs
                  : withSpecificationTabs
              }
            />
          }
        />
      </>
    );
  }

  return null;
};

const Asset = (props: any) => {
  const assetId = props.match.params.id.split('&');

  const { suite, property } = props?.location?.state || {};

  const { asset, area, loadingAsset } = useAssetAPI(
    suite?.id,
    assetId[0],
    assetId[1]
  );

  if (loadingAsset || !asset) {
    return <AssetSkeletonView suite={suite} property={property} />;
  }

  return <AssetInner {...props} asset={asset} area={area} />;
};

export default Asset;
